import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import ReorderSettings from "@/pages/ReorderSettings";

const URGENCY_BADGE = {
  CRITICO: "bg-red-100 text-red-800 border border-red-300",
  ALTO: "bg-orange-100 text-orange-800 border border-orange-300",
  MEDIO: "bg-amber-100 text-amber-800 border border-amber-300",
  OK: "bg-emerald-100 text-emerald-800 border border-emerald-300",
};
const CONF_BADGE = {
  actual: "bg-emerald-50 text-emerald-800",
  estimated: "bg-amber-50 text-amber-800",
  insufficient_data: "bg-neutral-100 text-neutral-600",
};

export default function SmartReorder() {
  const [data, setData] = useState({ suggestions: [], count: 0 });
  const [selected, setSelected] = useState({});
  const [filters, setFilters] = useState({ urgency: "", supplier: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, sup] = await Promise.all([
        api.get(`/reorder/suggestions?include_ok=false${filters.urgency ? `&urgency=${filters.urgency}` : ""}`),
        api.get("/suppliers"),
      ]);
      setData(r.data); setSuppliers(sup.data || []);
    } catch (e) { toast.error("Errore caricamento"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filters.urgency]);

  const filtered = useMemo(() => (data.suggestions || []).filter((s) => {
    if (filters.supplier && s.supplier_id !== filters.supplier) return false;
    if (filters.search && !(s.product_name || "").toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  }), [data, filters]);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const selectAll = () => {
    const all = {}; filtered.forEach((s) => (all[s.product_id] = true));
    setSelected(all);
  };
  const clearAll = () => setSelected({});

  const createDrafts = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error("Seleziona almeno un articolo"); return; }
    setCreating(true);
    try {
      const rows = ids.map((pid) => {
        const s = filtered.find((x) => x.product_id === pid);
        return { product_id: pid, quantity: s?.recommended_qty };
      });
      const r = await api.post("/reorder/create-drafts", { suggestions: rows });
      const w = (r.data.warnings || []).length;
      const c = (r.data.created || []).length;
      toast.success(`Bozze create: ${c}${w ? ` · avvisi: ${w}` : ""}`);
      setSelected({}); load();
    } catch (e) { toast.error("Errore creazione bozze"); }
    setCreating(false);
  };

  const counts = { CRITICO: 0, ALTO: 0, MEDIO: 0, OK: 0 };
  (data.suggestions || []).forEach((s) => (counts[s.urgency] = (counts[s.urgency] || 0) + 1));
  const totalSelectedCost = filtered
    .filter((s) => selected[s.product_id])
    .reduce((sum, s) => sum + (s.estimated_cost || 0), 0);

  return (
    <div data-testid="smart-reorder-page">
      <PageHeader eyebrow="Operatività" title="Riordino Intelligente"
        subtitle="Proposte basate su consumo, lead time e ordini già in corso. Nulla viene inviato automaticamente." />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard testid="stat-critico" label="Critici" value={counts.CRITICO || 0} />
        <StatCard testid="stat-alto" label="Alti" value={counts.ALTO || 0} />
        <StatCard testid="stat-medio" label="Medi" value={counts.MEDIO || 0} />
        <StatCard testid="stat-total" label="Da ordinare" value={data.count} />
        <StatCard testid="stat-selected-cost" label="Selezionati €" value={fmtEUR(totalSelectedCost)} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input placeholder="Cerca…" value={filters.search}
               onChange={(e) => setFilters({ ...filters, search: e.target.value })}
               className="max-w-xs" data-testid="filter-search" />
        <Select value={filters.urgency || "all"} onValueChange={(v) => setFilters({ ...filters, urgency: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40" data-testid="filter-urgency"><SelectValue placeholder="Urgenza" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le urgenze</SelectItem>
            <SelectItem value="CRITICO">CRITICO</SelectItem>
            <SelectItem value="ALTO">ALTO</SelectItem>
            <SelectItem value="MEDIO">MEDIO</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.supplier || "all"} onValueChange={(v) => setFilters({ ...filters, supplier: v === "all" ? "" : v })}>
          <SelectTrigger className="w-52" data-testid="filter-supplier"><SelectValue placeholder="Fornitore" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i fornitori</SelectItem>
            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="open-reorder-settings">Impostazioni</Button>
        <Button variant="outline" onClick={selectAll}>Seleziona tutto</Button>
        <Button variant="outline" onClick={clearAll}>Deseleziona</Button>
        <Button onClick={createDrafts} disabled={creating} className="bg-neutral-900 text-[#FDFBF7]" data-testid="create-drafts-btn">
          {creating ? "Creazione…" : `Crea bozza (${Object.keys(selected).filter((k) => selected[k]).length})`}
        </Button>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="reorder-table">
          <thead>
            <tr className="text-left label-eyebrow">
              <th className="p-3"></th>
              <th className="p-3">Articolo</th>
              <th className="p-3">Categoria</th>
              <th className="p-3 text-right">Stock</th>
              <th className="p-3 text-right">Consumo 7g / 30g</th>
              <th className="p-3 text-right">Copertura</th>
              <th className="p-3 text-right">Lead</th>
              <th className="p-3 text-right">In arrivo</th>
              <th className="p-3 text-right">Reorder pt</th>
              <th className="p-3 text-right">Proposta</th>
              <th className="p-3">Urgenza</th>
              <th className="p-3">Confidence</th>
              <th className="p-3">Fornitore</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="p-6 text-center text-neutral-400">Caricamento…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={13} className="p-6 text-center text-neutral-400">Nessun riordino necessario 🎉</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.product_id} className="border-t border-[#E2D9C8] hover:bg-[#FDFBF7]"
                  data-testid={`row-${s.product_id}`}>
                <td className="p-3">
                  <Checkbox checked={!!selected[s.product_id]} onCheckedChange={() => toggle(s.product_id)}
                            data-testid={`check-${s.product_id}`} />
                </td>
                <td className="p-3">
                  <div className="font-medium">{s.product_name}</div>
                  {s.open_order_warning && <div className="text-xs text-amber-700 mt-0.5">
                    ⚠ Già in ordine aperto ({s.open_order_warning.status})
                  </div>}
                  {s.anomaly && <div className="text-xs text-blue-700 mt-0.5">📈 {s.anomaly}</div>}
                  <details className="mt-1"><summary className="text-xs text-neutral-500 cursor-pointer">perché</summary>
                    <ul className="text-xs text-neutral-600 mt-1 list-disc list-inside">
                      {(s.explanation || []).map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  </details>
                </td>
                <td className="p-3 text-xs text-neutral-500">{s.category}</td>
                <td className="p-3 text-right">{s.on_hand} <span className="text-xs text-neutral-500">{s.unit}</span>
                  {(s.incoming?.planned || s.incoming?.pending) ? (
                    <div className="text-[10px] text-neutral-500">bozze {s.incoming?.planned || 0} · pending {s.incoming?.pending || 0}</div>
                  ) : null}
                </td>
                <td className="p-3 text-right text-xs">{s.consumption?.avg_7} / {s.consumption?.avg_30}</td>
                <td className="p-3 text-right">{s.days_of_cover !== null ? `${s.days_of_cover}g` : "—"}</td>
                <td className="p-3 text-right">
                  {s.lead_time_status === "missing" ? (
                    <div className="text-[10px] text-amber-700">non config.</div>
                  ) : (
                    <>{Math.round(s.lead_time_days)}g</>
                  )}
                </td>
                <td className="p-3 text-right">{s.confirmed_incoming || 0}</td>
                <td className="p-3 text-right">{s.reorder_point === null ? "—" : s.reorder_point}</td>
                <td className="p-3 text-right">
                  <div className="font-medium">{s.recommended_qty}</div>
                  {s.rounded_from !== s.recommended_qty && (
                    <div className="text-[10px] text-neutral-500">da {s.rounded_from} · pack {s.pack_size}</div>
                  )}
                </td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${URGENCY_BADGE[s.urgency]}`}>{s.urgency}</span></td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${CONF_BADGE[s.confidence]}`}>{s.confidence}</span></td>
                <td className="p-3 text-xs text-neutral-500">{s.supplier_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReorderSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
